import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const INTRO =
  "We respect your privacy and are committed to handling personal data with transparency, care and discretion. This Privacy Policy explains how Akkas Music collects, uses and protects personal information in connection with this website, our services and our communications.";

const SECTIONS = [
  { n: "1", title: "Controller", blocks: [
    { p: "The controller responsible for data processing on this website is:" },
    { p: "Süleyman Akkas\ntrading as Akkas Music\nWeidenweg 24\n53227 Bonn\nGermany" },
    { p: "Email: info@akkasmusic.com\nPhone: +49 177 7742473" },
  ]},
  { n: "2", title: "General Information", blocks: [
    { p: "We process personal data only where necessary to operate this website, present our services, analyse performance, improve user experience, manage consent preferences and respond to enquiries." },
    { p: "Personal data means any information relating to an identified or identifiable natural person. This may include your IP address, browser information, device data, or information you provide voluntarily." },
  ]},
  { n: "3", title: "Hosting", blocks: [
    { p: "This website is hosted by united-domains GmbH." },
    { p: "In connection with the hosting of this website, personal data may be processed to ensure technical delivery, stability and security. This may include in particular:" },
    { ul: ["IP address", "date and time of access", "browser type and version", "operating system", "referring URL", "requested pages and files", "access status"] },
  ]},
  { n: "4", title: "Server Log Files", blocks: [
    { p: "When you visit this website, certain information is collected automatically for technical reasons. This data is processed in particular to ensure the functionality, security and stability of the website and to detect and prevent misuse." },
    { p: "The legal basis for this processing is our legitimate interest in the secure and reliable operation of our website." },
  ]},
  { n: "5", title: "Consent Management", blocks: [
    { p: "This website uses Usercentrics as its consent management platform. Usercentrics helps us collect, manage and document user consent for cookies, analytics and marketing technologies in accordance with applicable data protection requirements." },
    { p: "When you interact with the consent banner, information about your consent choices, device, browser and related technical data may be processed in order to store your preferences and demonstrate compliance." },
    { p: "You can change or withdraw your consent preferences at any time through the consent settings made available on this website." },
  ]},
  { n: "6", title: "Cookies and Similar Technologies", blocks: [
    { p: "This website uses cookies and similar technologies." },
    { p: "Some cookies are technically necessary for the operation and security of the website. Other cookies and similar technologies are used only with your consent, in particular for analytics, marketing and performance-related purposes." },
    { p: "You can accept, reject or adjust your preferences at any time via the consent management tool on this website." },
  ]},
  { n: "7", title: "Google Fonts", blocks: [
    { p: "This website uses Google Fonts to ensure a consistent and visually refined presentation across devices and browsers." },
    { p: "Depending on the technical implementation, requests may be made to Google servers in order to load font resources." },
    { p: "The legal basis for the use of Google Fonts is either your consent, where required, or our legitimate interest in a consistent and high-quality presentation of our website." },
  ]},
  { n: "8", title: "Google Analytics", blocks: [
    { p: "This website uses Google Analytics to better understand how visitors use the website and to improve performance, content and user experience." },
    { p: "Google Analytics may process information such as:" },
    { ul: ["pages viewed", "time spent on pages", "technical device and browser information", "approximate location derived from technical data", "interactions with website content"] },
    { p: "The use of Google Analytics on this website takes place only on the basis of your consent." },
  ]},
  { n: "9", title: "Meta Pixel", blocks: [
    { p: "This website uses the Meta Pixel to measure the effectiveness of advertising, understand user interactions and improve marketing performance." },
    { p: "Through the Meta Pixel, information may be collected about your use of the website, including page visits, interactions, technical device information, browser data and related events. If you are logged into services provided by Meta, this information may potentially be associated with your account in accordance with Meta's own privacy terms." },
    { p: "The legal basis for the use of the Meta Pixel is your consent." },
  ]},
  { n: "10", title: "Contact by Email", blocks: [
    { p: "If you contact us by email, we process the information you provide solely for the purpose of responding to your request and handling the related communication." },
    { p: "This may include:" },
    { ul: ["your name", "your email address", "your company name", "the content of your message", "any additional information you voluntarily provide"] },
    { p: "The legal basis for this processing is either the performance of pre-contractual steps, the performance of a contract, or our legitimate interest in responding to business enquiries." },
  ]},
  { n: "11", title: "Newsletter", blocks: [
    { p: "We may offer a newsletter in the future in order to provide updates about our work, services, projects or relevant insights." },
    { p: "If a newsletter is introduced, subscription will take place only on the basis of your consent. If legally required, a double opt-in procedure will be used to verify your subscription." },
    { p: "We currently anticipate using Mailchimp as our newsletter provider. In that case, personal data such as your email address and, where applicable, your name may be processed through Mailchimp for the purpose of sending newsletters and managing subscriptions." },
    { p: "Further details regarding the newsletter provider, the specific processing operations and the applicable retention periods will be added or updated before the newsletter goes live." },
  ]},
  { n: "12", title: "Legal Bases for Processing", blocks: [
    { p: "Where applicable, we process personal data on the basis of:" },
    { ul: ["your consent", "the performance of a contract or steps prior to entering into a contract", "compliance with legal obligations", "our legitimate interests in operating a secure, functional and professionally presented website and in managing communications efficiently"] },
  ]},
  { n: "13", title: "Data Retention", blocks: [
    { p: "We retain personal data only for as long as necessary for the purposes for which it was collected or as long as we are legally required to retain it." },
    { p: "Once the relevant purpose no longer applies and no statutory retention obligation exists, the data will be deleted or restricted in accordance with applicable law." },
  ]},
  { n: "14", title: "International Data Transfers", blocks: [
    { p: "Some third-party providers used on this website may process personal data outside the European Union or the European Economic Area." },
    { p: "Where this is the case, we aim to ensure that such transfers take place only in accordance with applicable data protection law and that appropriate safeguards are in place." },
  ]},
  { n: "15", title: "Your Rights", blocks: [
    { p: "Under applicable data protection law, you may have the right to:" },
    { ul: ["request information about your personal data", "request access to your stored data", "request rectification of inaccurate data", "request erasure of your data", "request restriction of processing", "receive your data in a structured, commonly used and machine-readable format", "object to certain processing activities", "withdraw consent at any time with future effect", "lodge a complaint with a competent supervisory authority"] },
  ]},
  { n: "16", title: "Changes to This Privacy Policy", blocks: [
    { p: "We may update this Privacy Policy from time to time to reflect legal, technical or business developments. The version published on this website at the time of your visit shall apply." },
  ]},
];

function Block({ block }) {
  if (block.ul) {
    return (
      <ul className="mt-2 space-y-1.5 pl-1">
        {block.ul.map((li, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed">
            <span className="text-[#C5A880] mt-[2px]">—</span>
            <span>{li}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="text-sm leading-relaxed whitespace-pre-line mt-3">{block.p}</p>;
}

export default function PrivacyPolicy() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="privacy-policy-button"
          className="link-underline font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-[#FBF9F5] pb-1"
          style={{ backgroundImage: "linear-gradient(#FBF9F5,#FBF9F5)" }}
        >
          Privacy Policy
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="privacy-policy-modal"
        className="max-w-2xl bg-[#FBF9F5] text-[#5C5650] border-none rounded-none p-8 sm:p-10 max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display font-light text-3xl sm:text-4xl tracking-tight text-[#121110]">
            Privacy Policy
          </DialogTitle>
        </DialogHeader>

        <p className="mt-5 text-sm leading-relaxed font-light">{INTRO}</p>

        <div className="mt-8 space-y-8">
          {SECTIONS.map((sec) => (
            <section key={sec.n} className="border-t border-[#121110]/10 pt-6">
              <h3 className="font-serif-display font-light text-2xl text-[#121110] mb-1">
                <span className="font-mono-accent text-xs text-[#C5A880] mr-3 align-middle">
                  {sec.n}
                </span>
                {sec.title}
              </h3>
              {sec.blocks.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </section>
          ))}
        </div>

        <p className="mt-8 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8C857B]">
          Last updated: April 22, 2026
        </p>
      </DialogContent>
    </Dialog>
  );
}
